// Page: ManageStockVideos
// Purpose: Admin list and filters for stock videos. Supports search, tag
// filters, sorting, pagination, preview, edit, and delete.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { ConfirmationModal } from '../../../components/ui/modals/confirmation-modal.jsx';
import { StockVideoModal } from '../../../components/admin/stockVideos/StockVideoModal';
import Footer from '../../../components/shared/Footer';
import { Video, Search, X, Filter, ArrowUpDown, ChevronDown, ArrowLeft, Edit, Trash2, Play } from 'lucide-react';
import { STOCK_VIDEO_CATEGORY_LIST } from '../../../data/tags/tagEnums';
import { getAllStockVideosForAdmin, deleteStockVideo } from '../../../api/media/stockVideosApi';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function ManageStockVideos() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is the admin
  const isAdmin = isUserAdmin(user);

  // State for stock videos management
  const [stockVideos, setStockVideos] = useState([]);
  const [filteredStockVideos, setFilteredStockVideos] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingStockVideos, setLoadingStockVideos] = useState(false);

  // Filter and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    stockVideoId: null,
    title: '',
    isDeleting: false
  });

  // Video modal state
  const [videoModal, setVideoModal] = useState({
    isOpen: false,
    stockVideo: null
  });

  // Refs for dropdowns
  const sortDropdownRef = useRef(null);

  // Check if user is an admin, if not, redirect to home page
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      }
      setIsLoading(false);
    } else if (user === null) {
      // No user, redirect to login
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  // Load stock videos
  const loadStockVideos = async (page = 1, resetList = false) => {
    if (loadingStockVideos) return;
    
    setLoadingStockVideos(true);
    
    try {
      const options = {
        page,
        limit: 25,
        search: searchQuery,
        tags: selectedTags.join(','),
        sortBy
      };

      const response = await getAllStockVideosForAdmin(options);
      
      if (response) {
        const newStockVideos = resetList ? response.stockVideos : [...stockVideos, ...response.stockVideos];
        
        setStockVideos(newStockVideos);
        setFilteredStockVideos(newStockVideos);
        setTotalCount(response.totalCount || 0);
        setCurrentPage(response.currentPage || page);
        setTotalPages(response.totalPages || 1);
        setHasMore(response.hasMore || false);
      }
    } catch (error) {
      console.error('Error loading stock videos:', error);
      addToast('Failed to load stock videos. Please try again.', 'error');
    } finally {
      setLoadingStockVideos(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (!isLoading && isAdmin) {
      loadStockVideos(1, true);
    }
  }, [isLoading, isAdmin, searchQuery, selectedTags, sortBy]);

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  // Handle tag filter
  const handleTagToggle = (tag) => {
    setSelectedTags(prev => {
      const newTags = prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag];
      setCurrentPage(1);
      return newTags;
    });
  };

  // Handle sort change
  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
    setShowSortDropdown(false);
  };

  // Handle load more
  const handleLoadMore = () => {
    if (hasMore && !loadingStockVideos) {
      loadStockVideos(currentPage + 1, false);
    }
  };

  // Handle delete stock video
  const handleDeleteStockVideo = (stockVideoId, title) => {
    setDeleteModal({
      isOpen: true,
      stockVideoId,
      title,
      isDeleting: false
    });
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!deleteModal.stockVideoId) return;

    setDeleteModal(prev => ({ ...prev, isDeleting: true }));

    try {
      await deleteStockVideo(deleteModal.stockVideoId);
      
      addToast('The stock video has been deleted successfully.', 'success');

      // Close modal and refresh the list
      setDeleteModal({ isOpen: false, stockVideoId: null, title: '', isDeleting: false });
      loadStockVideos(1, true);
    } catch (error) {
      console.error('Error deleting stock video:', error);
      addToast('Failed to delete the stock video. Please try again.', 'error');
      setDeleteModal(prev => ({ ...prev, isDeleting: false }));
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, stockVideoId: null, title: '', isDeleting: false });
  };

  // Handle video preview
  const handlePreviewVideo = (stockVideo) => {
    setVideoModal({
      isOpen: true,
      stockVideo: stockVideo
    });
  };

  const closeVideoModal = () => {
    setVideoModal({
      isOpen: false,
      stockVideo: null
    });
  };

  // Handle edit
  const handleEditStockVideo = (stockVideoId) => {
    navigate(`/app/private/editStockVideo/${stockVideoId}`);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading manage stock videos..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Redirect happens in useEffect
  }

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'popular', label: 'Most Popular' },
    { value: 'random', label: 'Random' }
  ];

  return (
    <div className="p-6 max-w-[1600px] mx-auto pb-20">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <button
            onClick={() => navigate('/app/private/admin/stock-videos')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mr-4 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded-lg p-1"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span className="text-sm font-medium">Back to Stock Videos</span>
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Video className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Manage Stock Videos</h1>
          </div>
          <button
            onClick={() => navigate('/app/private/createStockVideo')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Video className="h-4 w-4 mr-2" />
            Create Stock Video
          </button>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Edit, update or delete existing stock videos. Total: {totalCount} videos
        </p>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search stock videos by title, tags, or content..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-700 rounded-md leading-5 bg-white dark:bg-gray-900 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-gray-100 sm:text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => handleSearch('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>

        {/* Filter and Sort Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Filter Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {selectedTags.length > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                {selectedTags.length}
              </span>
            )}
          </button>

          {/* Sort Dropdown */}
          <div className="relative" ref={sortDropdownRef}>
            <button
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <ArrowUpDown className="h-4 w-4 mr-2" />
              {sortOptions.find(option => option.value === sortBy)?.label || 'Sort'}
              <ChevronDown className="h-4 w-4 ml-2" />
            </button>

            {showSortDropdown && (
              <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleSortChange(option.value)}
                      className={`block w-full text-left px-4 py-2 text-sm ${
                        sortBy === option.value
                          ? 'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tag Filters */}
        {showFilters && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Filter by Tags:</h3>
            <div className="flex flex-wrap gap-2">
              {STOCK_VIDEO_CATEGORY_LIST.map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagToggle(tag)}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    selectedTags.includes(tag)
                      ? 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200'
                      : 'bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
            {selectedTags.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setSelectedTags([])}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stock Videos Grid */}
      {loadingStockVideos && stockVideos.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner message="Loading stock videos..." />
        </div>
      ) : stockVideos.length === 0 ? (
        <div className="text-center py-12">
          <Video className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No stock videos found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {searchQuery || selectedTags.length > 0 
              ? "Try adjusting your search or filters"
              : "Get started by creating your first stock video"
            }
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/app/private/createStockVideo')}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Video className="h-4 w-4 mr-2" />
              Create Stock Video
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {stockVideos.map((stockVideo) => (
            <div
              key={stockVideo.id}
              className="bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:bg-white/5 dark:hover:bg-gray-800/20 transition-all"
            >
              <div className="flex items-start justify-between">
                {/* Left side - Thumbnail and content */}
                <div className="flex items-start space-x-4 flex-1 min-w-0">
                  {/* Thumbnail */}
                  <div className="flex-shrink-0">
                    {stockVideo.thumbnailUrl ? (
                      <div 
                        className="relative cursor-pointer group"
                        onClick={() => handlePreviewVideo(stockVideo)}
                      >
                        <img
                          src={stockVideo.thumbnailUrl}
                          alt={stockVideo.title}
                          className="h-20 w-32 object-cover rounded-lg border border-gray-200 dark:border-gray-600 transition-opacity group-hover:opacity-75"
                        />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="bg-black/60 rounded-full p-2">
                            <Play className="h-6 w-6 text-white" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="h-20 w-32 bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => handlePreviewVideo(stockVideo)}
                      >
                        <Video className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <Video className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                        {stockVideo.title}
                      </h3>
                    </div>
                    
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                      <span>Created: {new Date(stockVideo.createdAt).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>Views: {stockVideo.totalClicks || 0}</span>
                    </div>

                    {stockVideo.tags && stockVideo.tags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {stockVideo.tags.slice(0, 5).map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                          >
                            {tag}
                          </span>
                        ))}
                        {stockVideo.tags.length > 5 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                            +{stockVideo.tags.length - 5} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right side - Action buttons */}
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleEditStockVideo(stockVideo.id)}
                    className="inline-flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    title="Edit Stock Video"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  
                  <button
                    onClick={() => handleDeleteStockVideo(stockVideo.id, stockVideo.title)}
                    className="inline-flex items-center p-2 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    title="Delete Stock Video"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Load More Button */}
          {hasMore && (
            <div className="flex justify-center pt-6">
              <button
                onClick={handleLoadMore}
                disabled={loadingStockVideos}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {loadingStockVideos ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Loading...</span>
                  </>
                ) : (
                  'Load More'
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Stock Video"
        message={`Are you sure you want to delete "${deleteModal.title}"? This action cannot be undone and will also remove the video files from storage.`}
        confirmText={deleteModal.isDeleting ? "Deleting..." : "Delete"}
        confirmVariant="danger"
        isLoading={deleteModal.isDeleting}
      />

      {/* Video Preview Modal */}
      <StockVideoModal
        isOpen={videoModal.isOpen}
        onClose={closeVideoModal}
        stockVideo={videoModal.stockVideo}
      />

      <Footer />
    </div>
  );
} 